import React, { createContext, useContext, useState } from 'react';
import { BaseRequestError } from 'utils/api/request';
import { getAuthToken } from 'utils/cartHelpers';
import { STORE_ROOT } from 'utils/constants/urls';
import { isBrowser } from 'utils/helpers';

import { tracker } from '../../../utils/trackers';
import { VisibleForm } from '../MembershipCheckoutForms';
import { AccountFormData, BaseFormData } from '../MembershipSubmissionForm';

type MembershipsResponse = {
  id: string;
  type: string;
  user_id: string;
  order_id: string;
  status: string;
  program_plan_id: string;
};

function isAccountFormData(
  data: BaseFormData | AccountFormData,
): data is AccountFormData {
  return 'firstName' in data && 'lastName' in data;
}

const transformFromData = (
  data: BaseFormData | AccountFormData,
  visibleForm: VisibleForm,
) => {
  const formData = new FormData();

  const isUserSignUp = String(visibleForm === 'account' ? true : false);

  formData.append('variant_slug', data.variantSlug);
  formData.append('wants_marketing', String(!!data.marketingTerms));
  formData.append('payment[payment_token]', data.paymentToken);
  formData.append('user[email]', data.email);
  formData.append('user[password]', data.password);
  formData.append('user[new_record]', isUserSignUp);
  formData.append('user[terms_and_conditions]', String(data.programTerms));

  if (data.promoCode) {
    formData.append('promotion_code', data.promoCode);
  }

  if (isAccountFormData(data)) {
    formData.append('user[first_name]', data.firstName);
    formData.append('user[last_name]', data.lastName);
  }

  return formData;
};

type MembershipCheckoutContextType = {
  joinMembership: (
    payload: JoinMembershipPayload,
  ) => Promise<MembershipsResponse | void>;
  program?: string | null;
  setProgram: (value: string) => void;
  redirectedToConfirmation: boolean;
  setRedirectedToConfirmation: (value: boolean) => void;
};

type JoinMembershipPayload = {
  data: any;
  formType: VisibleForm;
  userData: {
    userId: number | null;
    userEmail: string | null;
  };
};

export const MembershipCheckoutContext =
  createContext<MembershipCheckoutContextType>({
    joinMembership: async () => {},
    program: null,
    setProgram: () => {},
    redirectedToConfirmation: false,
    setRedirectedToConfirmation: () => {},
  });

export const useMembershipCheckout = () =>
  useContext(MembershipCheckoutContext);

export const MembershipCheckoutProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [redirectedToConfirmation, setRedirectedToConfirmation] =
    useState(false);
  const [program, setProgram] = useState<string | null>(null);

  const joinMembership = async (
    payload: JoinMembershipPayload,
  ): Promise<MembershipsResponse | void> => {
    try {
      const {
        data,
        formType,
        userData: { userId, userEmail },
      } = payload;

      const transformedData = transformFromData(data, formType);

      const authToken = userId ? await getAuthToken() : null;
      const response = await fetch(`${STORE_ROOT}/aapi/v3/memberships/`, {
        method: 'POST',
        headers: authToken
          ? {
              Authorization: `Bearer ${authToken}`,
            }
          : undefined,
        body: transformedData,
        ...(!userId && { credentials: 'include' as RequestCredentials }),
      });
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        throw new BaseRequestError(
          `Request failed with status code ${response.status}`,
          response,
          responseData,
        );
      }

      const membershipsResponse = responseData as MembershipsResponse;

      if (isBrowser()) {
        tracker.trackMembershipPurchase({
          program: program || 'Program not provided',
          userEmail: String(userEmail || transformedData.get('email')),
          userId: String(userId || membershipsResponse.user_id),
        });

        setRedirectedToConfirmation(true);
      }

      return Promise.resolve(membershipsResponse);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  return (
    <MembershipCheckoutContext.Provider
      value={{
        joinMembership,
        program,
        setProgram,
        redirectedToConfirmation,
        setRedirectedToConfirmation,
      }}
    >
      {children}
    </MembershipCheckoutContext.Provider>
  );
};
